import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import B2bFileUploadApi, {
  UploadTenantFileResponse
} from 'api/b2b/B2bFileUploadApi'
import OrbitSession from 'hooks/useCookies'

import { TenantDetails } from '..'

export function UploadTdpDocuments({
  tenantDetails
}: {
  tenantDetails: TenantDetails
}) {
  const navigate = useNavigate()
  const token = OrbitSession.get('faberToken')
  const tenantId = tenantDetails.tenantId

  type FormValues = {
    companyLogo: File | null
    registrationCert: File | null
    taxCert: File | null
    signatoryDoc: File | null
  }

  type UploadStatus = {
    [key: string]: {
      loading: boolean
      error: string | null
      uploaded: boolean
    }
  }

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      companyLogo: null,
      registrationCert: null,
      taxCert: null,
      signatoryDoc: null
    }
  })

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    companyLogo: { loading: false, error: null, uploaded: false },
    registrationCert: { loading: false, error: null, uploaded: false },
    taxCert: { loading: false, error: null, uploaded: false },
    signatoryDoc: { loading: false, error: null, uploaded: false }
  })

  const documentTypeMap: Record<
    string,
    'b_logo' | 'b_cert' | 't_cert' | 'a_sign'
  > = {
    companyLogo: 'b_logo',
    registrationCert: 'b_cert',
    taxCert: 't_cert',
    signatoryDoc: 'a_sign'
  }

  const createUploadFunction = (key: string) => {
    return async (file: File) => {
      const documentType = documentTypeMap[key]

      // Create FormData
      const formData = new FormData()
      formData.append('files', file)

      // Create query string
      const queryString = new URLSearchParams({
        tenant_id: tenantId,
        document_type: documentType
      }).toString()

      return B2bFileUploadApi.uploadTenantDocuments(
        formData,
        queryString,
        token
      )
    }
  }

  // TODO: **--------Create a individual company muatation for each file type-------**
  const companyLogoMutation = useMutation({
    mutationFn: createUploadFunction('companyLogo'),
    onMutate: () => {
      setUploadStatus((prev) => ({
        ...prev,
        companyLogo: { loading: true, error: null, uploaded: false }
      }))
    },
    onSuccess: (data: UploadTenantFileResponse) => {
      if (data.status === 'success') {
        setUploadStatus((prev) => ({
          ...prev,
          companyLogo: { loading: false, error: null, uploaded: true }
        }))
      } else {
        setUploadStatus((prev) => ({
          ...prev,
          companyLogo: {
            loading: false,
            error: data.error || data.message || 'Upload failed',
            uploaded: false
          }
        }))
      }
    },
    onError: (error: { message: string }) => {
      setUploadStatus((prev) => ({
        ...prev,
        companyLogo: {
          loading: false,
          error: error.message || 'Failed to upload company logo',
          uploaded: false
        }
      }))
    }
  })

  const registrationCertMutation = useMutation({
    mutationFn: createUploadFunction('registrationCert'),
    onMutate: () => {
      setUploadStatus((prev) => ({
        ...prev,
        registrationCert: { loading: true, error: null, uploaded: false }
      }))
    },
    onSuccess: (data: UploadTenantFileResponse) => {
      if (data.status === 'success') {
        setUploadStatus((prev) => ({
          ...prev,
          registrationCert: { loading: false, error: null, uploaded: true }
        }))
      } else {
        setUploadStatus((prev) => ({
          ...prev,
          registrationCert: {
            loading: false,
            error: data.error || data.message || 'Upload failed',
            uploaded: false
          }
        }))
      }
    },
    onError: (error: { message: string }) => {
      setUploadStatus((prev) => ({
        ...prev,
        registrationCert: {
          loading: false,
          error: error.message || 'Failed to upload registration certificate',
          uploaded: false
        }
      }))
    }
  })

  const taxCertMutation = useMutation({
    mutationFn: createUploadFunction('taxCert'),
    onMutate: () => {
      setUploadStatus((prev) => ({
        ...prev,
        taxCert: { loading: true, error: null, uploaded: false }
      }))
    },
    onSuccess: (data: UploadTenantFileResponse) => {
      if (data.status === 'success') {
        setUploadStatus((prev) => ({
          ...prev,
          taxCert: { loading: false, error: null, uploaded: true }
        }))
      } else {
        setUploadStatus((prev) => ({
          ...prev,
          taxCert: {
            loading: false,
            error: data.error || data.message || 'Upload failed',
            uploaded: false
          }
        }))
      }
    },
    onError: (error: { message: string }) => {
      setUploadStatus((prev) => ({
        ...prev,
        taxCert: {
          loading: false,
          error: error.message || 'Failed to upload tax certificate',
          uploaded: false
        }
      }))
    }
  })

  const signatoryDocMutation = useMutation({
    mutationFn: createUploadFunction('signatoryDoc'),
    onMutate: () => {
      setUploadStatus((prev) => ({
        ...prev,
        signatoryDoc: { loading: true, error: null, uploaded: false }
      }))
    },
    onSuccess: (data: UploadTenantFileResponse) => {
      if (data.status === 'success') {
        setUploadStatus((prev) => ({
          ...prev,
          signatoryDoc: { loading: false, error: null, uploaded: true }
        }))
      } else {
        setUploadStatus((prev) => ({
          ...prev,
          signatoryDoc: {
            loading: false,
            error: data.error || data.message || 'Upload failed',
            uploaded: false
          }
        }))
      }
    },
    onError: (error: { message: string }) => {
      setUploadStatus((prev) => ({
        ...prev,
        signatoryDoc: {
          loading: false,
          error: error.message || 'Failed to upload signatory document',
          uploaded: false
        }
      }))
    }
  })

  // Maping the mutations to keys
  const mutations = {
    companyLogo: companyLogoMutation,
    registrationCert: registrationCertMutation,
    taxCert: taxCertMutation,
    signatoryDoc: signatoryDocMutation
  }

  const handleFileUpload = async (data: FormValues) => {
    const fileFields = [
      { key: 'companyLogo', file: data.companyLogo },
      { key: 'registrationCert', file: data.registrationCert },
      { key: 'taxCert', file: data.taxCert },
      { key: 'signatoryDoc', file: data.signatoryDoc }
    ]

    // Filtering the file that exist
    const filesToUpload = fileFields.filter(({ file }) => file !== null)

    // If there are no files to upload, we can return early
    if (filesToUpload.length === 0) {
      toast.error('Please select at least one document to upload.', {
        autoClose: 5000,
        hideProgressBar: false
      })
      return
    }

    const uploadPromises = filesToUpload.map(({ key, file }) => {
      const mutation = mutations[key as keyof typeof mutations]
      return mutation.mutateAsync(file!)
    })

    try {
      const results = await Promise.allSettled(uploadPromises)

      const successCount = results.filter(
        (result) =>
          result.status === 'fulfilled' &&
          (result.value as UploadTenantFileResponse).status === 'success'
      ).length

      const failureCount = results.length - successCount

      if (failureCount === 0) {
        console.log('All uploads successful!')
      } else {
        console.log(
          `${successCount} uploads successful, ${failureCount} failed`
        )
      }
    } catch (error) {
      console.error('Unexpected error during upload:', error)
    }
  }

  const onSubmit = (data: FormValues) => {
    if (!tenantId) {
      toast.error(
        'Please complete the previous steps before uploading documents.',
        {
          autoClose: 5000,
          hideProgressBar: false
        }
      )
      return
    }

    const hasUploadedFiles = Object.values(uploadStatus).some(
      (status) => status.uploaded
    )

    // The file is uploaded now we can navigate away
    if (hasUploadedFiles) {
      navigate('/hq/b2b')
    }
    // If there are files to upload, we can upload them
    else {
      handleFileUpload(data)
    }
  }

  const renderFileUpload = (
    name: keyof FormValues,
    label: string,
    gridSize: { md: number; xs: number } = { md: 6, xs: 12 }
  ) => {
    const status = uploadStatus[name]

    return (
      <Grid item md={gridSize.md} xs={gridSize.xs}>
        <Box>
          <Typography sx={{ mb: 1, fontWeight: 500 }}>{label}</Typography>
          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              position: 'relative',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'grey.50'
              },
              ...(status.uploaded && {
                borderColor: 'success.main',
                backgroundColor: 'success.50'
              }),
              ...(status.error && {
                borderColor: 'error.main',
                backgroundColor: 'error.50'
              })
            }}
          >
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <input
                  type='file'
                  // disabled={status.loading || mutation.isPending}
                  onChange={(e) => field.onChange(e.target.files?.[0] ?? null)}
                />
              )}
            />

            {status.uploaded && (
              <Typography
                variant='caption'
                sx={{ color: 'success.main', display: 'block', mt: 1 }}
              >
                ✓ Uploaded successfully
              </Typography>
            )}
          </Box>

          {status.error && (
            <Alert severity='error' sx={{ mt: 1 }}>
              {status.error}
            </Alert>
          )}
        </Box>
      </Grid>
    )
  }

  const hasUploadedFiles = Object.values(uploadStatus).some(
    (status) => status.uploaded
  )
  const buttonText = hasUploadedFiles ? 'Save' : 'Upload'
  const isUploading = Object.values(uploadStatus).some(
    (status) => status.loading
  )

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
          <Grid container spacing={3}>
            {renderFileUpload('companyLogo', 'Company Logo')}
            {renderFileUpload(
              'registrationCert',
              'Business Registration Certificate',
              { md: 4, xs: 12 }
            )}
            {renderFileUpload(
              'taxCert',
              'Tax Identification Certificate (e.g. VAT / PAN / GST)',
              { md: 4, xs: 12 }
            )}
            {renderFileUpload(
              'signatoryDoc',
              'Authorized Signatory ID Proof (e.g., Passport, Driving License)',
              { md: 4, xs: 12 }
            )}
          </Grid>

          <Box
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              pt: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              marginTop: '20px'
            }}
          >
            <Button
              type='submit'
              variant='contained'
              disableElevation
              disabled={isUploading}
              size='large'
              sx={{
                minWidth: 120,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              {isUploading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Uploading...
                </>
              ) : (
                buttonText
              )}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  )
}
